/**
 * ============================================================================
 * Ring2All Platform & Frontend Subsystem
 * 
 * @file TenantFormView.tsx
 * @author Ring2All Team
 * @copyright Ring2All LLC
 * 
 * @description
 * Multi-tab tenant management form with tenant information, domain configuration, and user access management in a unified interface. 🔁 Last Updated: 2025-01-XX by Ring2All Team
 * ============================================================================
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Building, Search, User, Globe, Settings, Copy, Plus, DollarSign } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useFixedActionBar } from '../../hooks/useFixedActionBar';
import FixedActionBar from '../../components/ui/FixedActionBar';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { ModuleFormHeader } from '../../components/layouts';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { navigateToTenantDuplicate } from './tenantsActions';
import { useTenantData, DEFAULT_TENANT_FORM, DEFAULT_DOMAIN_FORM, DEFAULT_USER_FORM } from './hooks/useTenantData';
import { useTenantForm } from './hooks/useTenantForm';
import { useTenantSubmit } from './hooks/useTenantSubmit';
import { useQuickList } from './hooks/useQuickList';
import { useUserSearch } from './hooks/useUserSearch';
import { TenantFormFields } from './components/TenantFormFields';
import { DomainFormFields } from './components/DomainFormFields';
import { UserFormFields } from './components/UserFormFields';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import type { Tenant } from './types';

export default function TenantFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const duplicateId = searchParams.get('duplicate');
  const resetParam = searchParams.get('_reset');
  const mode = id === 'new' || duplicateId ? 'create' : 'edit';

  const { t } = useI18n();
  const { showInfo } = useNotifications();
  useFixedActionBar(true);

  // Hooks
  const {
    formData,
    setFormData,
    originalData,
    setOriginalData,
    domainData,
    setDomainData,
    userData,
    setUserData,
    sanitizeTenantFormState,
    isPrimary
  } = useTenantData(mode, id, duplicateId, resetParam);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'tenant' | 'domain' | 'user'>('tenant');
  const [useCurrentUser, setUseCurrentUser] = useState(true);
  const [isCreatingNewUser, setIsCreatingNewUser] = useState(true);
  const [selectedUserOption, setSelectedUserOption] = useState<'search' | 'create' | null>('create');

  const {
    hasChanges,
    handleInputChange,
    handleDomainInputChange,
    handleUserInputChange
  } = useTenantForm(
    formData,
    setFormData,
    domainData,
    setDomainData,
    userData,
    setUserData,
    originalData,
    setErrors
  );

  const {
    handleSubmit,
    handleDelete,
    isSubmitting,
    isDeleting
  } = useTenantSubmit(
    mode,
    id,
    formData,
    domainData,
    userData,
    useCurrentUser,
    sanitizeTenantFormState,
    setFormData,
    setOriginalData,
    () => {
      const newErrors: Record<string, string> = {};
      if (!formData.name.trim()) {
        newErrors.name = t('tenants.errors.nameRequired');
      }
      if (!formData.defaultLocale.trim()) {
        newErrors.defaultLocale = t('tenants.errors.defaultLocaleRequired');
      }
      if (!formData.timezone.trim()) {
        newErrors.timezone = t('tenants.errors.timezoneRequired');
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    hasChanges
  );

  const {
    showQuickList,
    setShowQuickList,
    quickListSearch,
    setQuickListSearch,
    filteredQuickList,
    isQuickListLoading,
    handleItemSelect,
    handleClose: handleQuickListClose
  } = useQuickList(mode === 'edit' && !duplicateId ? id : null);

  const {
    showUserList,
    setShowUserList,
    userListSearch,
    setUserListSearch,
    availableUsers
  } = useUserSearch();

  // Reset user option selection when using current user
  useEffect(() => {
    if (useCurrentUser) {
      setSelectedUserOption(null);
      setIsCreatingNewUser(false);
    } else {
      setSelectedUserOption('create');
      setIsCreatingNewUser(true);
    }
  }, [useCurrentUser]);

  // Handle user selection
  const handleUserSelect = useCallback((selectedUser: {
    username: string;
    email: string;
    fullName?: string;
    department?: string;
    locale?: string;
    timezone?: string;
    startupDialog?: string;
    totpEnabled?: boolean;
    isActive?: boolean;
    profileId?: string;
    logProfileId?: string;
    tenantIds?: string[];
  }) => {
    setUserData({
      username: selectedUser.username,
      email: selectedUser.email,
      password: '',
      fullName: selectedUser.fullName || '',
      department: selectedUser.department || '',
      locale: selectedUser.locale || 'es',
      timezone: selectedUser.timezone || 'UTC',
      startupDialog: selectedUser.startupDialog || 'dashboard',
      totpEnabled: selectedUser.totpEnabled || false,
      isActive: selectedUser.isActive !== undefined ? selectedUser.isActive : true,
      profileId: selectedUser.profileId || '',
      logProfileId: selectedUser.logProfileId || '',
      tenantIds: selectedUser.tenantIds || []
    });
    setIsCreatingNewUser(false);
    setSelectedUserOption('search');
    setShowUserList(false);
    setUserListSearch('');
  }, [setUserData, setShowUserList, setUserListSearch]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    if (mode === 'edit') {
      setFormData(originalData ? { ...originalData } : { ...DEFAULT_TENANT_FORM });
    } else {
      setFormData({ ...DEFAULT_TENANT_FORM });
      setDomainData({ ...DEFAULT_DOMAIN_FORM });
      setUserData({ ...DEFAULT_USER_FORM });
      setUseCurrentUser(true);
    }
    setErrors({});
  }, [mode, originalData, setFormData, setDomainData, setUserData]);

  // Handle save internal (wraps handleSubmit to return Tenant)
  const handleSaveInternal = useCallback(async (): Promise<Tenant | undefined> => {
    // hasChanges validation removed

    const fakeEvent = {
      preventDefault: () => { },
      stopPropagation: () => { }
    } as React.FormEvent;

    const savedData = await handleSubmit(fakeEvent);
    return savedData;
  }, [handleSubmit, hasChanges, showInfo, t, mode, id, formData]);

  // Save with action hook
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/admin/multi-tenant/tenants',
    newPath: '/admin/multi-tenant/tenants/new',
    getEditPath: (savedData) => `/admin/multi-tenant/tenants/${savedData.id}`,
    mode,
    storageKey: 'tenants-save-action',
  });

  // Handle delete confirmation
  const confirmDelete = useCallback(async () => {
    await handleDelete();
    setShowDeleteModal(false);
    // Navigate to list after successful delete
    navigate('/admin/multi-tenant/tenants');
  }, [handleDelete, navigate]);

  return (
    <div className="p-6">
      {/* Fixed Header */}
      <ModuleFormHeader
        icon={<Building className="h-5 w-5 text-primary" />}
        title={mode === 'create' ? t('tenants.addTenantForm') : `${t('tenants.editTenant')} - ${formData.name || ''}`}
        backButtonText={t('common.list')}
        onBack={() => navigate('/admin/multi-tenant/tenants')}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('tenants.quickList'),
            onClick: () => setShowQuickList(true)
          },
          ...(mode === 'edit' && id && id !== 'new' && !duplicateId
            ? [
              {
                icon: <Copy className="h-5 w-5 text-muted-foreground" />,
                tooltip: t('common.duplicate'),
                onClick: () => navigateToTenantDuplicate(navigate, id)
              }
            ]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('tenants.addTenant'),
            onClick: () => navigate('/admin/multi-tenant/tenants/new')
          }
        ]}
      />

      <div className="mt-8">
        {/* Modern Tabs - Only show tabs in create mode */}
        {mode === 'create' && (
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'tenant' | 'domain' | 'user')}>
              <TabsList className="w-full justify-start">
                <TabsTrigger
                  value="tenant"
                  icon={<Settings className="w-4 h-4" />}
                >
                  {t('tenants.tabs.tenant')}
                </TabsTrigger>
                <TabsTrigger
                  value="domain"
                  icon={<Globe className="w-4 h-4" />}
                >
                  {t('tenants.tabs.domain')}
                </TabsTrigger>
                <TabsTrigger
                  value="user"
                  icon={<User className="w-4 h-4" />}
                >
                  {t('tenants.tabs.user')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        )}

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tenant Information - Always show in edit mode, or when tenant tab is active in create mode */}
          {(mode === 'edit' || activeTab === 'tenant') && (
            <>
              <TenantFormFields
                formData={formData}
                errors={errors}
                onInputChange={handleInputChange}
                isPrimary={isPrimary}
              />
            </>
          )}

          {/* Tab 2: Domain Configuration */}
          {activeTab === 'domain' && (
            <DomainFormFields
              domainData={domainData}
              onInputChange={handleDomainInputChange}
            />
          )}

          {/* Tab 3: User Access */}
          {activeTab === 'user' && (
            <UserFormFields
              userData={userData}
              useCurrentUser={useCurrentUser}
              isCreatingNewUser={isCreatingNewUser}
              selectedUserOption={selectedUserOption}
              onInputChange={handleUserInputChange}
              onUseCurrentUserChange={setUseCurrentUser}
              onSelectCreateNew={() => {
                setUserData({ ...DEFAULT_USER_FORM });
                setIsCreatingNewUser(true);
                setSelectedUserOption('create');
              }}
              onSelectSearch={() => {
                setShowUserList(true);
                setSelectedUserOption('search');
              }}
            />
          )}
        </form>
      </div>

      {/* Quick List Modal */}
      <QuickSearchModal
        isOpen={showQuickList}
        onClose={handleQuickListClose}
        title={t('tenants.quickListTitle', 'Quick List - Select Tenant')}
        items={filteredQuickList.filter((t): t is typeof t & { id: string } => !!t.id)}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={handleItemSelect}
        currentItemId={mode === 'edit' && !duplicateId ? id : null}
        renderItem={(tenant: Tenant, isCurrent: boolean) => (
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-foreground">{tenant.name}</p>
              {tenant.description && (
                <p className="text-sm text-muted-foreground">{tenant.description}</p>
              )}
              {tenant.slug && (
                <p className="text-xs text-muted-foreground">{tenant.slug}</p>
              )}
              {tenant.adminEmail && (
                <p className="text-xs text-muted-foreground">{tenant.adminEmail}</p>
              )}
            </div>
            {isCurrent && (
              <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded">
                {t('tenants.current', 'Current')}
              </span>
            )}
          </div>
        )}
        searchPlaceholder={t('tenants.quickListSearchPlaceholder', 'Search by name, slug, description or email')}
        emptyMessage={t('tenants.quickListPrompt', 'Start typing to search...')}
        noResultsMessage={t('tenants.quickListEmptySearch', 'No tenants found')}
        headerIcon={<Building className="h-5 w-5" />}
        minSearchChars={0}
      />

      {/* User List Modal */}
      <QuickSearchModal
        isOpen={showUserList}
        onClose={() => {
          setShowUserList(false);
          setUserListSearch('');
        }}
        title={t('tenants.userAccess.searchUser')}
        items={availableUsers.filter((u): u is typeof u & { id: string } => !!u.id)}
        isLoadingInitial={false}
        isSearching={false}
        searchValue={userListSearch}
        onSearchChange={setUserListSearch}
        onItemSelect={(item) => handleUserSelect(item as Parameters<typeof handleUserSelect>[0])}
        currentItemId={null}
        renderItem={(item) => {
          const user = item as { id?: string; username: string; email?: string; fullName?: string };
          return (
          <div>
            <p className="font-medium text-foreground">{user.username}</p>
            {user.email && (
              <p className="text-sm text-muted-foreground">{user.email}</p>
            )}
            {user.fullName && (
              <p className="text-xs text-muted-foreground">{user.fullName}</p>
            )}
          </div>
        );}}
        searchPlaceholder={t('tenants.userAccess.searchPlaceholder', 'Search by username, email or name')}
        emptyMessage={t('tenants.userAccess.emptyMessage', 'Start typing to search...')}
        noResultsMessage={t('tenants.userAccess.noResults', 'No users found')}
        headerIcon={<User className="h-5 w-5" />}
        minSearchChars={0}
      />

      {/* Fixed Action Bar */}
      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={mode === 'edit' ? () => setShowDeleteModal(true) : undefined}
        deleteDisabled={isPrimary}
        deleteTooltip={isPrimary ? t('tenants.cannotDeletePrimary') : undefined}
        isEditMode={mode === 'edit'}
        saveText={isSubmitting ? t('common.saving') : t('common.save')}
        cancelText={t('common.cancel')}
        deleteText={t('common.delete') || 'Delete'}
        hasChanges={hasChanges}
        isSubmitting={isSubmitting}
        saveDisabled={isSubmitting}
        isLoading={isSubmitting}
        useSaveDropdown={true}
        showSave={true}
        saveDropdownStorageKey="tenants-save-action"
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title={t('tenants.deleteConfirmTitle') || 'Delete Tenant'}
        message={t('tenants.deleteConfirmMessage') || 'Are you sure you want to delete this tenant? This action cannot be undone.'}
        confirmText={t('tenants.deleteConfirm') || t('common.delete') || 'Delete'}
        cancelText={t('common.cancel') || 'Cancel'}
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}
